// ---------------------------------------------------------------------------
// themeConfigs — a camada de SOBREPOSIÇÃO que o painel grava sobre o catálogo
// de temas do código.
//
// Blueprint: `docs/systems/admin-themes.md`.
//
// ## O que esta camada governa, e o que ela NUNCA governa
//
// Governa **venda**: preço, temporada, estar na vitrine, estar no destaque.
//
// Não governa **direito**. `resolveEquippedThemeId` não conhece este módulo e
// não pode passar a conhecer: quem comprou um tema não pode perdê-lo porque
// alguém mexeu no painel. É a mesma linha dos temas sazonais
// (`seasonal-themes.md` §3), e o critério de corte da Onda 1 foi escolhido para
// que ela nunca precise ser cruzada.
//
// ## Os três estados de cada campo
//
// `undefined` (campo ausente no doc) = "o painel não opinou, vale o código".
// Um valor = "passa a ser isto". E, só para `seasonId`, `null` = "sem
// temporada", que é um estado de produto (o tema passa a ser vendido o ano
// todo) e por isso é GRAVADO em vez de apagado.
//
// Colapsar ausente com `null` é o que tiraria do operador a capacidade de
// desfazer — e um painel que só sabe aplicar é um painel pela metade.
// ---------------------------------------------------------------------------

import type { ThemeDefinition } from "./themeRegistry";
import { THEMES } from "./themeRegistry";
import { adminSeasonWindow, isSeasonOpen, isSeasonWindowOpen } from "./themeSeasons";
import { isThemeEventOpen } from "./themeEvents";
import { isShowcaseLive } from "./showcaseWindow";

/** O doc de `theme_configs/{themeId}`, como o Firestore o devolve. */
export interface ThemeConfig {
  source?: "code" | "admin";
  price?: number;
  /** `null` gravado = sem temporada. Ausente = a do código. */
  seasonId?: string | null;
  storeEnabled?: boolean;
  showcasePinned?: boolean;
  /**
   * Janela do destaque, em ms. Cada ponta é independente e opcional: só
   * `showcaseFrom` é "a partir de", só `showcaseUntil` é "até", nenhuma é
   * permanente.
   *
   * ⚠️ Existe porque destaque com prazo e sem quem o desative é o BUG-39. Um
   * tema fixado "para sempre" depende de alguém lembrar de desmarcá-lo, e a
   * memória do operador é o que este painel existe para não exigir.
   */
  showcaseFrom?: number;
  showcaseUntil?: number;

  /**
   * Janela LIVRE da temporada, em chave de dia BR ("AAAA-MM-DD"), as duas
   * pontas **inclusivas** e independentes.
   *
   * Vence a temporada nomeada — ver `isThemeSellableNow`. Existe porque
   * `THEME_SEASONS` é código: sem estes dois campos, mover uma temporada ou
   * criar a segunda exige deploy, e o painel só sabia apontar para a única
   * temporada que alguém já tinha escrito à mão.
   */
  seasonFrom?: string;
  seasonUntil?: string;

  // ── Só em tema criado no painel (`source: "admin"`) ─────────────────────────
  label?: string;
  description?: string;
  /** Os 46 tokens derivados. É o que o runtime aplica inline no `<html>`. */
  tokens?: Record<string, string>;
  /** Hex plano para o share-studio, que não resolve CSS var nem gradiente. */
  exportPalette?: {
    background: string;
    surface: string;
    surfaceAlt: string;
    border: string;
    textPrimary: string;
    textMuted: string;
    accent: string;
    accentSecondary: string;
  };
  /** `draft` não é vendido nem equipável. Só `published` existe para o app. */
  status?: "draft" | "published";
}

/**
 * Os temas do painel que estão PUBLICADOS.
 *
 * ⚠️ `draft` fica de fora em todos os consumidores, e é por isso que a função
 * existe em vez de cada tela filtrar por conta própria: um rascunho que vaze
 * para a Loja é um tema à venda que ninguém revisou, e um que vaze para o
 * resolvedor é um tema equipável que não passou pelo portão de contraste.
 */
export function publishedAdminThemes(configs: ThemeConfigMap): {
  ids: string[];
  byId: Record<string, ThemeConfig>;
} {
  const byId: Record<string, ThemeConfig> = {};
  for (const [id, c] of Object.entries(configs)) {
    if (c.source === "admin" && c.status === "published" && c.tokens) byId[id] = c;
  }
  return { ids: Object.keys(byId), byId };
}

export type ThemeConfigMap = Readonly<Record<string, ThemeConfig>>;

/**
 * Espelho da banda validada no servidor (`functions/src/shared/schemas.ts`).
 * Travado por `themeConfigParity.test.ts`.
 *
 * Existe no client para a mesma coisa que o servidor: recusar um valor
 * impossível. A diferença é o papel — o client valida para ser **gentil** (diz
 * antes de você salvar), o servidor valida para ser **verdade** (é ele quem
 * debita). Só um dos dois pode ser removido sem consequência, e não é o de lá.
 */
export const THEME_PRICE_MIN = 50;
export const THEME_PRICE_MAX = 5000;

/** A sobreposição de preço é utilizável? Mesma regra do `themePriceFor`. */
function usablePrice(v: unknown): number | undefined {
  if (typeof v !== "number" || !Number.isInteger(v)) return undefined;
  if (v < THEME_PRICE_MIN || v > THEME_PRICE_MAX) return undefined;
  return v;
}

/**
 * Preço base vigente de um tema: a sobreposição, ou o do código.
 *
 * ⚠️ Isto é o preço ANTES do desconto PRO. O desconto segue morando em
 * `effectiveThemePrice` (`features/store/utils/themePricing.ts`), que espelha a
 * fórmula do servidor — dois lugares, dois papéis, e juntá-los faria o desconto
 * ser aplicado duas vezes no dia em que alguém chamasse os dois.
 */
export function basePriceFor(theme: ThemeDefinition, config?: ThemeConfig): number {
  return usablePrice(config?.price) ?? theme.price?.coins ?? 0;
}

/**
 * A temporada que de fato vale para o tema.
 *
 * Espelha `resolveSeasonId` do servidor. Os três estados vivem aqui inteiros.
 *
 * ⚠️ O `in` está aqui por CLAREZA de intenção ("o campo existe?"), não por
 * necessidade: medido, trocá-lo por `config?.seasonId !== undefined` é uma
 * mutação EQUIVALENTE, porque o Firestore nunca devolve um campo com valor
 * `undefined` — ele devolve o campo com valor, ou não devolve o campo. As três
 * formas possíveis (`undefined`, `{}`, `{seasonId: null}`) dão o mesmo
 * resultado nas duas escritas. Registrado para o próximo não perder tempo
 * caçando um teste que "não morde".
 */
export function effectiveSeasonId(
  theme: ThemeDefinition,
  config?: ThemeConfig,
): string | null {
  if (config && "seasonId" in config) return config.seasonId ?? null;
  return theme.season ?? null;
}

/** O tema pode ser COMPRADO agora, já aplicada a sobreposição? */
export function isThemeSellableNow(
  theme: ThemeDefinition,
  config: ThemeConfig | undefined,
  at: Date = new Date(),
): boolean {
  // Retirado da vitrine pelo painel vence tudo: é a alavanca de "tira isso do
  // ar agora", e ela não pode depender de mais nenhuma condição para funcionar.
  if (config?.storeEnabled === false) return false;

  // Tema de EVENTO não é vendido: existe enquanto o evento existe, e a data é
  // do código (`themeEvents.ts`), porque ela governa o DIREITO — o painel não
  // pode encurtar ou esticar o direito de quem está usando.
  if (theme.event) return isThemeEventOpen(theme.event, at);

  // 🔴 As datas do painel vencem a temporada nomeada, e a ordem é o desenho:
  // elas são a coisa mais específica que o operador consegue dizer, e foram
  // digitadas depois. Se a temporada vencesse, o formulário aceitaria as datas
  // e nada mudaria — a classe "salvei e não fez efeito".
  //
  // ⚠️ Espelhado em `isThemePurchasableNow` no servidor. Divergindo, a vitrine
  // anuncia uma janela e o débito cobra outra, e o sintoma seria "o botão
  // Comprar não faz nada".
  const janela = adminSeasonWindow(config);
  if (janela) return isSeasonWindowOpen(janela, at);

  const seasonId = effectiveSeasonId(theme, config);
  if (!seasonId) return true;
  // ⚠️ Segue caindo em `isSeasonOpen`, que falha FECHADA em temporada
  // desconhecida. Devolver `true` aqui inverteria isso e um typo passaria a
  // vender o tema para sempre, em silêncio.
  return isSeasonOpen(seasonId, at);
}

/**
 * Os temas que a Loja deve mostrar agora.
 *
 * ⚠️ Substitui `getStoreThemes(at)` nas superfícies que têm as configs em mãos.
 * A função do registry continua existindo e continua correta — ela é a resposta
 * **sem** painel, e portanto a queda quando a leitura do Firestore falha. Um
 * app que não conseguiu ler `theme_configs` mostra o catálogo do código, que é
 * a definição de degradar bem.
 */
export function storeThemesNow(
  configs: ThemeConfigMap,
  at: Date = new Date(),
): ThemeDefinition[] {
  return THEMES.filter(
    (t) =>
      (t.tier === "free" || t.tier === "coin-gated") &&
      isThemeSellableNow(t, configs[t.id], at),
  );
}

/**
 * Ids fixados no destaque pelo painel, na ordem do catálogo.
 *
 * ⚠️ Só entra quem está vendável agora. Fixar um tema e depois tirá-lo da
 * vitrine deixaria um card fantasma no destaque — a contradição é possível
 * (são dois campos independentes) e quem a resolve é a leitura, não o
 * formulário: proibir a combinação obrigaria o operador a lembrar da ordem dos
 * cliques.
 */
export function pinnedThemeIds(
  configs: ThemeConfigMap,
  at: Date = new Date(),
): string[] {
  return THEMES.filter((t) => {
    const c = configs[t.id];
    if (c?.showcasePinned !== true) return false;
    // A janela é o DESATIVADOR. Sem ela um destaque agendado apareceria antes
    // da hora e um vencido ficaria para sempre — que é o BUG-39.
    if (!isShowcaseLive({from: c.showcaseFrom, until: c.showcaseUntil}, at.getTime())) return false;
    return isThemeSellableNow(t, c, at);
  }).map((t) => t.id);
}
